import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { MessageSquarePlus } from "lucide-react";

import { activityStore } from "../../../../../../store/activity.store";

// activity.comment is varchar(1000) - the server rejects anything longer
const MAX_COMMENT_LENGTH = 1000;

interface AddCommentButtonProps {
  onClick: () => void;
  className?: string;
}

/**
 * Sits next to the activity type and opens the editor down in the comment slot. Rendered only
 * for rows without a comment - a row that has one is edited by clicking the comment itself.
 */
export const AddCommentButton = memo<AddCommentButtonProps>(({ onClick, className = "" }) => {
  const { formatMessage } = useIntl();
  const label = formatMessage({ id: 'ADD_COMMENT' });

  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      // no hover on a touch layout, so it stays visible below sm
      className={`text-muted-foreground/70 hover:text-foreground transition-opacity
        sm:opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100
        ${className}`}
      onClick={onClick}
    >
      <MessageSquarePlus className="h-3.5 w-3.5" />
    </button>
  );
});

AddCommentButton.displayName = 'AddCommentButton';

interface QuickCommentProps {
  activityId: string;
  // null for a record saved without a comment
  comment: string | null;
  editing: boolean;
  onEditingChange: (editing: boolean) => void;
  className?: string;
}

/**
 * The comment line, editable in place. The comment belongs to the activity rather than to the
 * interval, so editing it from one row updates every row that activity owns.
 */
const QuickComment = memo<QuickCommentProps>(({
  activityId,
  comment,
  editing,
  onEditingChange,
  className = "",
}) => {
  const { formatMessage } = useIntl();

  const updateComment = activityStore(state => state.updateComment);

  // a record with no comment arrives as null; the editor only ever deals in strings
  const saved = comment ?? "";

  const [value, setValue] = useState(saved);
  const [failed, setFailed] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  // a save triggered by Enter or Escape must not be repeated by the blur it causes
  const settled = useRef(false);

  useEffect(() => {
    if (editing) {
      settled.current = false;
      inputRef.current?.focus();
      inputRef.current?.select();
    } else {
      setValue(saved);
      setFailed(false);
    }
  }, [editing, saved]);

  const save = useCallback(async () => {
    const next = value.trim();
    onEditingChange(false);

    if (next === saved) {
      return;
    }

    const ok = await updateComment(activityId, next);
    if (!ok) {
      // keep what was typed so the edit is not lost with the failed request
      setFailed(true);
      setValue(next);
      onEditingChange(true);
    }
  }, [value, saved, activityId, updateComment, onEditingChange]);

  const cancel = useCallback(() => {
    setValue(saved);
    setFailed(false);
    onEditingChange(false);
  }, [saved, onEditingChange]);

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      settled.current = true;
      save();
    }
    if (event.key === 'Escape') {
      settled.current = true;
      cancel();
    }
  }, [save, cancel]);

  const handleBlur = useCallback(() => {
    if (!settled.current) {
      save();
    }
  }, [save]);

  if (editing) {
    return (
      <div className={className}>
        <input
          ref={inputRef}
          value={value}
          maxLength={MAX_COMMENT_LENGTH}
          placeholder={formatMessage({ id: 'ADD_COMMENT' })}
          className="w-full bg-transparent border-b border-primary text-sm text-foreground outline-none py-0.5"
          onChange={e => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
        />
        {failed && (
          <div className="text-xs text-red-600 mt-0.5">
            {formatMessage({ id: 'COMMENT_SAVE_ERROR' })}
          </div>
        )}
      </div>
    );
  }

  if (!saved) {
    return null;
  }

  return (
    <div
      role="button"
      tabIndex={0}
      title={formatMessage({ id: 'EDIT_COMMENT' })}
      className={`text-sm text-muted-foreground cursor-text hover:text-foreground ${className}`}
      onClick={() => onEditingChange(true)}
      onKeyDown={e => e.key === 'Enter' && onEditingChange(true)}
    >
      {saved}
    </div>
  );
});

QuickComment.displayName = 'QuickComment';

export default QuickComment;
